import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography,
} from '@mui/material';
import { Add, Remove } from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';

import { addToCart } from '../../store/slices/cartSlice';
import { getUserPhoneNumber } from '../../utils/phoneUtils';
import { calculatePaymentDetails } from '../../utils/paymentUtils';
import { lotBalance } from '../../utils/lotUtils';

// Lots list for a product. Prefers the product.lots array (multi-lot),
// falls back to the legacy flat lot fields as a single "Default Lot".
const getEffectiveLots = (product) => {
  if (!product) return [];
  if (product.lots && product.lots.length > 0) return product.lots;
  if (product.lotLength || product.lotWidth || product.lotNos || product.lotSft) {
    return [{
      lotId: 'default',
      lotName: 'Default Lot',
      lotLength: product.lotLength,
      lotWidth: product.lotWidth,
      lotNos: product.lotNos,
      lotSft: product.lotSft,
      lotLocation: product.lotLocation,
    }];
  }
  return [];
};

// SFT for one lot (prefer stored lotSft; fall back to L×W×N/144)
const computeLotSft = (lot) => {
  if (lot.lotSft) return parseFloat(lot.lotSft) || 0;
  return ((parseFloat(lot.lotLength) || 0) * (parseFloat(lot.lotWidth) || 0) * lotBalance(lot)) / 144;
};

/**
 * Shared "Book a Lot" dialog used by the product listing and product detail pages.
 * Lets the user pick how many lots to book, shows the 10% advance split, and adds
 * the lot booking to the cart (lot request is created after checkout).
 */
const BookLotDialog = ({ open, onClose, product, user }) => {
  const dispatch = useDispatch();
  const [lotUiQuantity, setLotUiQuantity] = useState(1);

  // Always start with 1 selected lot whenever the dialog opens / product changes
  useEffect(() => {
    if (open) setLotUiQuantity(1);
  }, [open, product?.id]);

  // Only lots with pieces remaining can be selected
  const availableLots = getEffectiveLots(product).filter(l => lotBalance(l) > 0);
  const numberOfAvailableLots = availableLots.length;
  // Clamp UI quantity to actual available lots (never let it exceed what exists)
  const clampedQty = Math.max(1, Math.min(lotUiQuantity, numberOfAvailableLots || 1));
  const selectedLots = availableLots.slice(0, clampedQty);
  // Max pieces = total across ALL available lots
  const maxPieces = availableLots.reduce((s, l) => s + lotBalance(l), 0);
  // Backend quantity = pieces in the selected lots
  const backendQuantity = selectedLots.reduce((s, l) => s + lotBalance(l), 0);
  const selectedSft = selectedLots.reduce((s, l) => s + computeLotSft(l), 0);
  const availableSft = availableLots.reduce((s, l) => s + computeLotSft(l), 0);
  // Price = selectedSft × pricePerSft — no further multiplication by quantity
  const actualPrice = Number(product?.discountedPrice || product?.price) || 0;
  const totalLotPrice = selectedSft * actualPrice;
  const advanceAmt = totalLotPrice * 0.10;
  const remainingAmt = totalLotPrice * 0.90;

  const handleContinue = () => {
    if (!product) return;
    if (!user) {
      toast.error('Please login to book a lot');
      return;
    }
    if (selectedLots.length === 0) {
      toast.error('No lots are currently available for this product.');
      return;
    }
    try {
      const p = product;
      const productWeight = p.weight || 1;
      const paymentDetails = calculatePaymentDetails(totalLotPrice, productWeight, true);
      const advanceGst = paymentDetails.gstAmount;

      const primaryLot = selectedLots[0] || null;
      const lotNames = selectedLots.map(l => l.lotName || 'Default Lot').join(', ');

      const lotRequestData = {
        customerId: user.id,
        customerName: user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim(),
        customerEmail: user.email,
        customerPhone: getUserPhoneNumber(user),

        sellerId: p.seller?.id || p.sellerId || 'default-seller',
        sellerName: p.seller?.name || p.sellerName || 'MOS Marketplace',
        sellerEmail: p.seller?.email || p.sellerEmail || '',
        sellerPhone: getUserPhoneNumber(p.seller),

        productId: p.id,
        productName: p.name,
        productDetails: p.description || '',
        productImageUrl: p.imageUrls?.[0] || '',
        category: p.category || '',
        subCategory: p.subCategory || '',
        productWeight: productWeight,

        // Lot Details — aggregated across all selected lots
        lotSize: `${selectedSft.toFixed(4)} sqft`,
        lotPercentage: 10,
        lotQuantity: backendQuantity,  // total pieces in selected lots
        lotSft: selectedSft,
        // One entry per booked lot; lotNos = slabs booked from that lot
        lots: selectedLots.map(l => ({
          lotId: l.lotId || 'default',
          lotLength: l.lotLength,
          lotWidth: l.lotWidth,
          lotNos: lotBalance(l),
        })),
        lotLocation: primaryLot?.lotLocation || p.lotLocation,

        baseAmount: totalLotPrice,
        advanceAmount: paymentDetails.advanceAmount,
        gstAmount: advanceGst,
        gstRate: 0.18,
        shippingCharges: 0,
        remainingAmount: totalLotPrice - paymentDetails.advanceAmount,
        isFreeShipping: true,

        message: `Lot booking request for ${p.name} (${clampedQty} lot${clampedQty > 1 ? 's' : ''}). Selected SFT: ${selectedSft.toFixed(4)}, Base: ₹${totalLotPrice.toFixed(2)}, Advance (10%): ₹${paymentDetails.advanceAmount.toFixed(2)}, GST on Advance: ₹${advanceGst.toFixed(2)}`,
        timeline: null,
        status: "PENDING_INSPECTION",
        priority: "MEDIUM",

        unitPrice: p.price,
        actualPrice: actualPrice,
        totalLotPrice: totalLotPrice,
      };

      // Lot request is created AFTER the order is successfully placed
      localStorage.setItem('pendingLotRequest', JSON.stringify(lotRequestData));

      const lotBookingProduct = {
        ...p,
        originalPrice: actualPrice,
        price: paymentDetails.advanceAmount,
        isLotBooking: true,
        advancePercentage: 10,
        lotSft: selectedSft,
        totalLotPrice: totalLotPrice,
        selectedLotId: primaryLot?.lotId || 'default',
        selectedLotName: lotNames,
        uiLotCount: clampedQty,
        paymentSplit: {
          advanceAmount: paymentDetails.advanceAmount,
          remainingAmount: totalLotPrice - paymentDetails.advanceAmount,
          totalAmount: totalLotPrice,
          advancePercentage: 10,
          remainingPercentage: 90,
        },
      };

      // quantity = total pieces across selected lots (stock-keeping unit)
      // (lot booking items are skipped in allocateInventory — stock reserved via lotRequest approval)
      dispatch(addToCart({
        product: lotBookingProduct,
        quantity: backendQuantity,
        isLotBooking: true,
      }));

      onClose();
      toast.success('Product added to cart for lot booking! Complete checkout to finalize your lot request.');
    } catch (error) {
      console.error('Error processing lot booking:', error);
      toast.error('Failed to process lot booking request. Please try again.');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h6" component="div">
          Book a Lot
        </Typography>
      </DialogTitle>
      <DialogContent>
        {product && (
          <Box sx={{ pt: 2 }}>
            <Typography variant="body1" gutterBottom>
              <strong>Product:</strong> {product.name}
            </Typography>
            <Typography variant="body1" gutterBottom>
              <strong>Price:</strong> ₹{actualPrice.toFixed(2)} per SFT
            </Typography>

            {/* ── Lot quantity selector ── */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2, mb: 1 }}>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>Number of Lots:</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', border: 1, borderColor: 'divider', borderRadius: 1 }}>
                <IconButton
                  size="small"
                  onClick={() => setLotUiQuantity(q => Math.max(1, q - 1))}
                  disabled={clampedQty <= 1}
                >
                  <Remove />
                </IconButton>
                <Typography sx={{ px: 2, fontWeight: 700, minWidth: 32, textAlign: 'center' }}>
                  {clampedQty}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => setLotUiQuantity(q => Math.min(q + 1, numberOfAvailableLots))}
                  disabled={clampedQty >= numberOfAvailableLots}
                >
                  <Add />
                </IconButton>
              </Box>
            </Box>

            {/* Max (total pieces) and SFT info */}
            <Box sx={{ display: 'flex', gap: 3, mb: 2, flexWrap: 'wrap' }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Max:</strong> {maxPieces} pieces
              </Typography>
              <Typography variant="body2" sx={{ color: 'secondary.main', fontWeight: 600 }}>
                Selected SFT: {selectedSft.toFixed(4)} SFT
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Available SFT: {availableSft.toFixed(4)} SFT
              </Typography>
            </Box>

            {/* Selected lots details */}
            {selectedLots.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Selected lots:</Typography>
                {selectedLots.map((lot, idx) => (
                  <Box key={lot.lotId || idx} sx={{ pl: 1, mb: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">
                      {lot.lotName || `Lot ${idx + 1}`}
                      {lot.lotLength && ` — ${lot.lotLength}″ × ${lot.lotWidth}″ × ${lotBalance(lot)} pcs`}
                      {` (${computeLotSft(lot).toFixed(4)} SFT)`}
                    </Typography>
                  </Box>
                ))}
                {selectedLots[0]?.lotLocation && (
                  <Typography variant="body2" color="text.secondary" sx={{ pl: 1 }}>
                    Location: {selectedLots[0].lotLocation}
                  </Typography>
                )}
              </Box>
            )}

            <Divider sx={{ my: 1.5 }} />

            {/* Price breakdown */}
            <Typography variant="body1" gutterBottom>
              <strong>Total Lot Price:</strong> ₹{totalLotPrice.toFixed(2)}
              <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                ({selectedSft.toFixed(4)} SFT × ₹{actualPrice.toFixed(2)})
              </Typography>
            </Typography>

            <Box sx={{ mt: 2, p: 2, bgcolor: 'primary.light', borderRadius: 1 }}>
              <Typography variant="body1" sx={{ color: 'primary.contrastText', textAlign: 'center' }}>
                Pay 10% advance to book. This secures your lot and will be adjusted against the final payment.
              </Typography>
            </Box>
            <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="h6" sx={{ color: 'primary.main' }}>
                Advance (10%): ₹{advanceAmt.toFixed(2)}
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ alignSelf: 'center' }}>
                Remaining (90%): ₹{remainingAmt.toFixed(2)}
              </Typography>
            </Box>

            <Box sx={{ mt: 2, p: 1.5, bgcolor: 'warning.light', borderRadius: 1, border: '1px solid', borderColor: 'warning.main' }}>
              <Typography variant="body2" sx={{ color: 'warning.contrastText', textAlign: 'center', fontStyle: 'italic' }}>
                Please note: Actual lot size may vary by up to 10% from the specified dimensions.
              </Typography>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 3 }}>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={handleContinue}
          variant="contained"
          color="primary"
          disabled={selectedLots.length === 0}
        >
          Continue
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BookLotDialog;
